var _State_instances, _State_matchTarget, _State_runNode;
import { __classPrivateFieldGet } from "tslib";
/* Copyright 2021, Milkdown by Mirone. */
import { parserMatchError } from '@milkdown/exception';
/**
 * State for parser.
 * Transform remark AST into prosemirror state.
 */
export class State {
    constructor(stack, schema, specMap) {
        this.stack = stack;
        this.schema = schema;
        this.specMap = specMap;
        _State_instances.add(this);
        /**
         * Transform a markdown string into prosemirror state.
         *
         * @param remark - The remark parser used.
         * @param markdown - The markdown string needs to be parsed.
         * @returns The state instance.
         */
        this.run = (remark, markdown) => {
            const tree = remark.runSync(remark.parse(markdown));
            this.next(tree);
            return this;
        };
        /**
         * Give the node or node list back to the state and the state will find a proper runner (by `match` method) to handle it.
         *
         * @param nodes - The node or node list needs to be handled.
         *
         * @returns The state instance.
         */
        this.next = (nodes = []) => {
            [nodes].flat().forEach((node) => __classPrivateFieldGet(this, _State_instances, "m", _State_runNode).call(this, node));
            return this;
        };
        /**
         * Parse current remark AST into prosemirror state.
         *
         * @returns Result prosemirror doc.
         */
        this.toDoc = () => this.stack.build();
        /**
         * Inject root node for prosemirror state.
         *
         * @param node - The target markdown node.
         * @param nodeType - The root prosemirror nodeType .
         * @param attrs - The attribute of root type.
         * @returns The state instance.
         */
        this.injectRoot = (node, nodeType, attrs) => {
            this.stack.openNode(nodeType, attrs);
            this.next(node.children);
            return this;
        };
        /**
         * Add a text type prosemirror node.
         *
         * @param text - Text string.
         * @returns The state instance.
         */
        this.addText = (text = '') => {
            this.stack.addText((marks) => this.schema.text(text, marks));
            return this;
        };
        /**
         * Add a node without open or close it.
         *
         * @remarks
         * It's useful for nodes which don't have content.
         *
         * @param nodeType - Node type of this node.
         * @param attrs - Attributes of this node.
         * @param content - Content of this node.
         *
         * @returns The added node.
         */
        this.addNode = (...args) => {
            this.stack.addNode(...args);
            return this;
        };
        /**
         * Open a node, and all nodes created after this method will be set as the children of the node until a `closeNode` been called.
         *
         * @remarks
         * You can imagine `openNode` as the left half of parenthesis and `closeNode` as the right half. For nodes have children, your runner should just take care of the node itself and let other runners to handle the children.
         *
         * @param nodeType - Node type of this node.
         * @param attrs - Attributes of this node.
         *
         * @returns
         */
        this.openNode = (...args) => {
            this.stack.openNode(...args);
            return this;
        };
        /**
         * Close current node.
         *
         * @returns The node closed.
         */
        this.closeNode = (...args) => {
            this.stack.closeNode(...args);
            return this;
        };
        /**
         * Open a mark, and all marks created after this method will be set as the children of the mark until a `closeMark` been called.
         *
         * @remarks
         * You can imagine `openMark` as the left half of parenthesis and `closeMark` as the right half. For nodes have children, your runner should just take care of the node itself and let other runners to handle the children.
         *
         * @param markType - Mark type of this mark.
         * @param attrs - Attributes of this mark.
         *
         * @returns
         */
        this.openMark = (...args) => {
            this.stack.openMark(...args);
            return this;
        };
        /**
         * Close target mark.
         *
         * @param markType - Mark type of this mark.
         *
         * @returns The mark closed.
         */
        this.closeMark = (...args) => {
            this.stack.closeMark(...args);
            return this;
        };
    }
}
_State_instances = new WeakSet(), _State_matchTarget = function _State_matchTarget(node) {
    const result = Object.values(this.specMap).find((x) => x.match(node));
    if (!result)
        throw parserMatchError(node);
    return result;
}, _State_runNode = function _State_runNode(node) {
    const { key, runner, is } = __classPrivateFieldGet(this, _State_instances, "m", _State_matchTarget).call(this, node);
    const proseType = this.schema[is === 'node' ? 'nodes' : 'marks'][key];
    runner(this, node, proseType);
};
//# sourceMappingURL=state.js.map